import { useEffect, useState } from "react";
import { NEGATIVE_PARAMETERS, DEFAULT_DECIMAL_PLACE } from "@ts/constants";
import { ICellRendererParams } from "ag-grid-community";
import { internationalizeNumericValue } from "@/ts/utils";
import { useSelector } from "react-redux";
import { IShowDeltaValues } from "@/ts/common-types";

interface IChangeCellRendererParams extends ICellRendererParams {
    hasPinnedColumnValue: boolean;
    hasPercent: boolean;
    showChangePercent: boolean;
    decimalPlace: number;
    suppressNullValueText?: string;
    hasSingleValue?: boolean; 
    hideInfinite?: boolean;
}

const ChangeInValueCellRenderer: React.FC<IChangeCellRendererParams> = (
    params
) => {

    const showDeltaValues = useSelector((state: IShowDeltaValues) => state.showDeltaValues.value);
    const numberOfDecimalPoints =
        typeof params.decimalPlace == "number"
            ? params.decimalPlace
            : DEFAULT_DECIMAL_PLACE;

    const spanTitle = `Compared with: ${
        params.data[`previous_${params.colDef.field}`] &&
        +params.data[`previous_${params.colDef.field}`] !== null &&
        isFinite(+params.data[`previous_${params.colDef.field}`])
            ? `${+(
                  +params.data[`previous_${params.colDef.field}`] || 0
              ).toFixed(numberOfDecimalPoints)}${params.hasPercent ? "%" : ""}`
            : params.data[`previous_${params.colDef.field}`] !== null &&
              !isNaN(+params.data[`previous_${params.colDef.field}`])
            ? "∞"
            : params.suppressNullValueText || ""
    }`;

    const defaultSpanClass = "text-nowrap float-end font-sm";
    const [spanClass, setSpanClass] = useState<string>("");
    const [spanTextContent, setSpanTextContent] = useState<string>(
        `${
            params.data[`${params.colDef.field}_change`] !== null &&
            isFinite(+params.data[`${params.colDef.field}_change`])
                ? `${+(
                      +params.data[`${params.colDef.field}_change`] || 0
                  ).toFixed(numberOfDecimalPoints)}${
                      params.showChangePercent ? "% " : " "
                  }`
                : "-"
        }`
    );

    useEffect(() => {
        if (
            !isNaN(parseFloat(params.data[`${params.colDef.field}_change`])) &&
            parseFloat(params.data[`${params.colDef.field}_change`]) > 0
        ) {
            setSpanClass(
                NEGATIVE_PARAMETERS.indexOf(params.colDef.field) === -1
                    ? " text-success"
                    : " text-danger"
            );

            // setSpanTextContent(spanTextContent + "▲");
            setSpanTextContent((prevValue) => {
                return prevValue + "▲";
            });
        } else if (
            !isNaN(parseFloat(params.data[`${params.colDef.field}_change`])) &&
            parseFloat(params.data[`${params.colDef.field}_change`]) < 0
        ) {
            setSpanClass(
                NEGATIVE_PARAMETERS.indexOf(params.colDef.field) === -1
                    ? " text-danger"
                    : " text-success"
            );

            // setSpanTextContent(spanTextContent + "▼");
            setSpanTextContent((prevValue) => {
                return prevValue + "▼";
            });
        } else {
            setSpanClass(" text-warning");
            // setSpanTextContent(spanTextContent + "-");
            setSpanTextContent((prevValue) => {
                return prevValue + "-";
            });
        }
        /**
         * !Params has been removed from here as it was multiplying the change values on clicking current renderer
         */
        // eslint-disable-next-line
    }, []);

    return (
        <>
            {!params.hasPinnedColumnValue && params.node.rowPinned ? null : (
                <div className="d-flex flex-column">
                    {+params.value !== null && isFinite(+params.value)
                        ? `${
                              params.hasPercent
                                  ? +(+params.value || 0).toFixed(
                                        numberOfDecimalPoints
                                    )
                                  : internationalizeNumericValue(
                                        +(+params.value || 0).toFixed(
                                            numberOfDecimalPoints
                                        )
                                    )
                          }${params.hasPercent ? "%" : ""}`
                        : params.value !== null && !isNaN(params.value) && !params?.hideInfinite
                        ? "∞"
                        : params.suppressNullValueText || ""}

                    {showDeltaValues && (params.hasSingleValue ? null : (
                        <span
                            className={defaultSpanClass + spanClass}
                            title={spanTitle}
                            style={{ marginTop: "-15px" }}
                        >
                            {spanTextContent}
                        </span>)
                    )}
                </div>
            )}
        </>
    );
};

export default ChangeInValueCellRenderer;
